<script setup>
import { cookieRef } from '@/@layouts/stores/config'
import AnalyticsFeedbackSentiment from '@/@core/components/feedback/AnalyticsFeedbackSentiment.vue'
import SentimentCoverageStrip from '@/@core/components/feedback/SentimentCoverageStrip.vue'

definePage({
  meta: {
    name: 'sentiment',
    subject: 'feedback.menu',
    action: 'sentiment',
  },
})

// Mirrors the dashboard's local date helpers (src/pages/app/feedback/dashboard/index.vue)
// rather than a shared util, since the dashboard doesn't expose one either.
const addDays = (date, days) => {
  const result = new Date(date)

  result.setDate(result.getDate() + days)

  return result
}

const customDateFormat = date => {
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
}

const dateRange = ref()

const fromDate = ref(customDateFormat(addDays(new Date(), -30)))
const toDate = ref(customDateFormat(new Date()))

// No unit dropdown here — the dashboard's own unit AppSelect is commented out,
// so this page instead reads whatever unit was last selected there.
const unitId = ref(cookieRef('selectedUnit') ?? null)

const dateRangeOptions = ref({
  mode: 'range',
  maxDate: new Date(),
  dateFormat: 'Y-m-d',
  altInput: true,
  onChange: (selectedDates, dateStr) => {
    fromDate.value = customDateFormat(selectedDates[0])
    if (selectedDates[1])
      toDate.value = customDateFormat(selectedDates[1])
    else
      toDate.value = customDateFormat(selectedDates[0])

    dateRange.value = dateStr
  },
  defaultDate: [customDateFormat(addDays(new Date(), -30)), customDateFormat(new Date())],
})

// AnalyticsFeedbackSentiment owns this fetch; this page just consumes the
// coverage block it emits, so the endpoint is only called once per load.
const coverage = ref(null)

// The widget is this page's only source of coverage, so if it can never produce
// one the strip has to be told — otherwise it shows "Checking coverage…" for
// the rest of the session.
const coverageUnavailable = ref(false)

// The widget also owns the aspect list. The evidence panel picks from this copy
// instead of fetching its own, so it stays usable when the widget is showing its
// below-threshold progress state and there are no bubbles to click.
const aspects = ref([])

const selectedAspect = ref(null)

const onAspectSelected = payload => {
  selectedAspect.value = payload?.aspect ?? null
}

// A range/unit change remounts the widget with a fresh list; drop a selection
// the new list no longer contains so the panel's selector cannot show a label
// that is not in its own items.
watch(aspects, list => {
  if (list.length && selectedAspect.value && !list.some(a => a.aspect === selectedAspect.value))
    selectedAspect.value = null
})
</script>

<template>
  <div class="sentiment-page">
    <div class="sentiment-head d-flex align-center justify-space-between flex-wrap">
      <div class="sentiment-title">
        Feedback Sentiment
      </div>

      <div class="sentiment-range-picker">
        <AppDateTimePicker
          v-model="dateRange"
          placeholder="Date range"
          prepend-inner-icon="tabler-calendar"
          density="compact"
          :config="dateRangeOptions"
        />
      </div>
    </div>

    <SentimentCoverageStrip
      class="sentiment-cover"
      :coverage="coverage"
      :unavailable="coverageUnavailable"
    />

    <!--
      The mock's two-column region: 1.65fr beside 1fr with a 20px gutter
      (ref L170). Vuetify's 12-column grid cannot express 1.65:1 — lg="7"/lg="5"
      is 1.4:1 — and its gutter is fixed at 24px, so this is a plain CSS grid.
    -->
    <div class="sentiment-cols">
      <div class="sentiment-stack">
        <!--
          AnalyticsFeedbackSentiment reads its from/to/unit-id props only at
          setup time and does not refetch when they change, so the key forces a
          remount on every filter change. Deliberately NOT wrapped in AppLazy:
          this card sits below the fold behind a tall queue card, and as the
          page's only fetcher of /dashboard/feedbacks/sentiment, deferring its
          mount left the coverage strip above it spinning until the user
          scrolled past everything that strip exists to qualify. The key alone
          gives the remount; AppLazy only ever contributed the laziness.
        -->
        <AnalyticsFeedbackSentiment
          :key="`${unitId}-${fromDate}-${toDate}`"
          title="Aspects mentioned"
          class="sentiment-aspects"
          chart-aspect="tall"
          :from="fromDate"
          :to="toDate"
          :unit-id="unitId"
          :selected-aspect="selectedAspect"
          @update:feedback-table-filter="onAspectSelected"
          @update:coverage="coverage = $event"
          @update:aspects="aspects = $event"
          @update:unavailable="coverageUnavailable = $event"
        />

        <!--
          Two-way, but written out rather than as `v-model:aspect`: the prop is
          named `selected-aspect` to match AnalyticsFeedbackSentiment's, so the
          same page state reads the same way on both cards. The semantics are
          identical to the v-model sugar.
        -->
        <SentimentClosureFunnel
          :from="fromDate"
          :to="toDate"
          :unit-id="unitId"
          :selected-aspect="selectedAspect"
          @update:aspect="selectedAspect = $event"
        />
      </div>

      <!--
        The quotes panel sticks beside the taller left column. `position: sticky`
        has to sit on this INNER div rather than on the grid item: the grid is
        `align-items: start`, so the column is only as tall as its content and a
        sticky element filling it would have no room to travel.
      -->
      <div class="sentiment-sticky">
        <SentimentEvidencePanel
          v-model:aspect="selectedAspect"
          :aspects="aspects"
          :from="fromDate"
          :to="toDate"
          :unit-id="unitId"
        />
      </div>
    </div>

    <SentimentSilentComplaints
      class="sentiment-silent"
      :from="fromDate"
      :to="toDate"
      :unit-id="unitId"
      :coverage="coverage"
    />
  </div>
</template>

<style lang="scss" scoped>
/*
 * Every literal in this file is a measured value from the reference mock
 * (docs/superpowers/plans/redesign-exact-match-report.md records the pairs).
 * Vuetify's own scale does not carry 14.5px/22px/10px-radius, so where a theme
 * token matches the mock it is used, and where none does the measured number is
 * written out.
 */

/* ref L120-121: .head margin-bottom 20px, gap 12px 20px. */
.sentiment-head {
  gap: 12px 20px;
  margin-block-end: 20px;
}

/* ref L121: 24px/36px, weight 500, letter-spacing -.1px, heading ink. */
.sentiment-title {
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-size: 24px;
  font-weight: 500;
  letter-spacing: -0.1px;
  line-height: 36px;
}

/*
 * 340px still clips the longest realistic value ("September 1, 2026 to
 * September 30, 2026") by a few pixels — measured in the browser, not assumed.
 * 360px keeps a small margin over the 350px exact fit. The mock's button is
 * content-sized at 322px, but it is a button with no caret; an input has to
 * reserve room for its longest value.
 */
.sentiment-range-picker {
  flex: 0 0 360px;
  inline-size: 360px;
}

.sentiment-range-picker :deep(.v-input) {
  inline-size: 100%;
}

/* ref L122: .range is 42px tall with an 8px radius. */
.sentiment-range-picker :deep(.v-field) {
  border-radius: 8px;
  block-size: 42px;

  /*
   * This icon is ours (AppDateTimePicker has no prepend icon elsewhere), so
   * there's no house convention to inherit. --v-field-padding-start also
   * drives the real flatpickr input's own inline padding (its scss uses
   * padding-inline with this one var for both sides), so raising it doubles
   * as the icon-to-text gap fix: 6px measured too tight for 15px text, 9px
   * reads comfortably and still leaves ~30px of clear margin on the longest
   * realistic value.
   */
  --v-field-padding-start: 9px;
}

/*
 * The icon ships at 1.5rem (22.5px against this app's 15px root) beside
 * 15px/23px input text — a 1.5x ratio that reads as oversized next to it.
 * 20px sits with the text instead. Scoped here rather than in
 * AppDateTimePicker, which many other pages use without this icon.
 */
.sentiment-range-picker :deep(.v-field__prepend-inner .v-icon) {
  font-size: 20px;
}

/*
 * .v-field__field defaults to align-items: flex-start, so the 40px input row
 * is pinned to the top of the 42px field (2px left over at the bottom) while
 * the icon's own wrapper centers independently — the two centres land 0.5px
 * apart. Centering this flex container instead removes the asymmetric-padding
 * dependency, so the drift can't grow if the field's height ever changes.
 */
.sentiment-range-picker :deep(.v-field__field) {
  align-items: center;
}

@media (max-width: 599px) {
  .sentiment-range-picker {
    flex: 1 1 100%;
    inline-size: 100%;
  }
}

/* ref L45: --r-card is 10px; Vuetify's default card radius is 6px. */
.sentiment-page :deep(.v-card) {
  border-radius: 10px;
}

/* ref L134: .cover margin-bottom 20px. */
.sentiment-cover {
  margin-block-end: 20px;
}

/* ref L170: the two-column region. */
.sentiment-cols {
  display: grid;
  align-items: start;
  gap: 20px;
  grid-template-columns: minmax(0, 1.65fr) minmax(340px, 1fr);
  margin-block-start: 20px;
}

/* Same 20px rhythm as .sentiment-cover/.sentiment-cols: this card now sits
   below the two-column region instead of above it. */
.sentiment-silent {
  margin-block-start: 20px;
}

/* ref L131: .stack gap 20px. */
.sentiment-stack {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/*
 * ref L284-287: the columns stack at 1300px and the panel stops sticking.
 * Below that a paginated panel taller than the viewport would pin its own
 * pagination controls off-screen with no way to scroll to them.
 */
@media (min-width: 1301px) {
  .sentiment-sticky {
    position: sticky;
    overflow-y: auto;
    max-block-size: calc(100vh - 84px - 24px);
    inset-block-start: 84px;
  }
}

@media (max-width: 1300px) {
  .sentiment-cols {
    grid-template-columns: minmax(0, 1fr);
  }
}

/*
 * The aspects card is the one widget on this page that the feedback dashboard
 * also renders (src/pages/app/feedback/dashboard/index.vue). Everything the
 * mock asks for that the dashboard must not inherit is applied from here, not
 * from the component, so the dashboard's copy is untouched.
 */
.sentiment-aspects {
  // ref L128: .card-h padding 22px 24px 0.
  :deep(.v-card-item) {
    padding: 22px 24px 0;
  }

  // This card matches its siblings: 17px/500/25.5px, same as .sc-title and
  // .fn-title.
  :deep(.aspect-card-title) {
    font-size: 17px;
    font-weight: 500;
    line-height: 25.5px;
  }

  // This card matches its siblings: 14px on a 21px leading, same as
  // .sc-subtitle and .fn-subtitle.
  :deep(.v-card-subtitle) {
    font-size: 14px;
    line-height: 21px;
  }

  // ref L185: .viz sits 20px below the header behind a full-bleed rule.
  :deep(.v-divider) {
    margin-block-start: 20px;
  }

  :deep(.v-card-text) {
    padding: 0;
  }

  /*
   * ref L186 + L729: the mock pads the drawing by 12px and sizes the viewBox
   * from `viz.clientWidth - 24`, i.e. from the padded box, so the SVG user
   * units are 1:1 with CSS pixels. The padding therefore goes on the container
   * — the element the resize observer measures, whose contentRect already
   * excludes it — and not on the SVG, where it would leave the viewBox 24px
   * wider than the box it paints into and shrink every fitted label by 3%.
   */
  :deep(.aspect-bubbles) {
    padding: 12px;
  }

  // ref L200: .legend.
  :deep(.aspect-legend) {
    gap: 8px 22px;
    padding: 4px 24px 24px;
    margin-block-start: 0;
    font-size: 14.5px;
    line-height: 21.75px;
  }
}
</style>
